import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../../Contexts/CompanyContext';
import { getProductDisplayInfo } from '../../Services/productService';
import { useFeaturedProducts } from '../../Hooks/useProductQuery';

/* ================= IMAGE COMPONENT ================= */

interface ThumbnailProps {
    src?: string;
    alt: string;
}

const ThumbnailWithPlaceholder: React.FC<ThumbnailProps> = ({ src, alt }) => {
    const [loaded, setLoaded] = useState(false);

    const imageSrc = src || 'asset/images/product-placeholder.png';

    return (
        <div className="product-thumb-wrapper">
            {!loaded && (
                <img
                    src="asset/images/product-placeholder.png"
                    alt="placeholder"
                    className="product-thumb-img"
                />
            )}
            <img
                src={imageSrc}
                alt={alt}
                className="product-thumb-img"
                style={{ display: loaded ? 'block' : 'none' }}
                onLoad={() => setLoaded(true)}
            />
        </div>
    );
};

/* ================= COMPONENT ================= */

const FeaturedProducts: React.FC = () => {
    const navigate = useNavigate();
    const { formatPrice } = useCompany();

    const { data: products = [], isLoading } = useFeaturedProducts();

    // ── Animation state ────────────────────────────────────────────────────────
    // `animated` controls whether the CSS animation class is applied to list
    // items. It starts false on every mount and flips to true the first time
    // this mount has data — whether that came from a live Firestore fetch or
    // instantly from the React Query cache.
    //
    // WHY ref + state:
    //   `hasAnimated` ref guards against re-triggering on every re-render
    //   (e.g. parent re-renders, formatPrice reference changing). Once flipped
    //   it stays true for the lifetime of this mount.
    //
    //   Because the ref lives on the component instance, it resets to false
    //   each time the component unmounts and remounts (user navigates away and
    //   back), so the entrance animation plays again on every fresh mount.
    const [animated, setAnimated] = useState(false);
    const hasAnimated = useRef(false);

    useEffect(() => {
        // products.length > 0 is the "data ready" signal. On a cache hit this
        // fires synchronously after mount (React flushes effects after paint),
        // so the animation still plays even though isLoading was never true.
        if (!hasAnimated.current && products.length > 0) {
            hasAnimated.current = true;
            setAnimated(true);
        }
    }, [products]);

    const handleProductClick = (productId: string) => {
        navigate(`/product/${productId}`);
    };

    /* ================= LOADING ================= */

    if (isLoading) {
        return (
            <div className="p-t-0">
                <h4 className="mtext-112 cl2 p-b-33">Featured Products</h4>
                <p className="stext-116 cl6">Loading...</p>
            </div>
        );
    }

    if (products.length === 0) {
        return null;
    }

    /* ================= RENDER ================= */

    return (
        <div className="p-t-0">
            <style>{`
                @keyframes fp-fade-in-up {
                    from { opacity: 0; transform: translateY(18px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .fp-item-enter {
                    opacity: 0;
                }
                .fp-item-enter.fp-item-visible {
                    animation: fp-fade-in-up 0.45s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>

            <h4 className="mtext-112 cl2 p-b-33">Featured Products</h4>

            <ul>
                {products.map((product, index) => {
                    const displayInfo = getProductDisplayInfo(product);
                    const imageUrl = product.images?.[0];

                    return (
                        <li
                            key={product.id}
                            className={`flex-w flex-t p-b-30 fp-item-enter ${animated ? 'fp-item-visible' : ''}`}
                            style={animated ? {
                                // Stagger each item 80 ms apart so they cascade
                                // in rather than all appearing simultaneously.
                                animationDelay: `${index * 80}ms`,
                            } : undefined}
                        >
                            {/* IMAGE */}
                            <a
                                onClick={() => handleProductClick(product.id)}
                                className="wrao-pic-w size-214 hov-ovelay1 m-r-20"
                                style={{ cursor: 'pointer' }}
                            >
                                <ThumbnailWithPlaceholder
                                    src={imageUrl}
                                    alt={product.name}
                                />
                            </a>

                            {/* CONTENT */}
                            <div className="size-215 flex-col-t p-t-8">
                                <a
                                    onClick={() => handleProductClick(product.id)}
                                    className="stext-116 cl8 hov-cl1 trans-04"
                                    style={{ cursor: 'pointer' }}
                                >
                                    {product.name}
                                </a>

                                <span className="stext-116 cl6 p-t-20">
                                    {formatPrice(displayInfo.price)}
                                </span>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default FeaturedProducts;

// import React, { useEffect, useState } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { useCompany } from '../../Contexts/CompanyContext';
// import {
//     fetchFeaturedProducts,
//     Product,
//     getProductDisplayInfo
// } from '../../Services/productService';

// /* ================= IMAGE COMPONENT ================= */

// interface ThumbnailProps {
//     src?: string;
//     alt: string;
// }

// const ThumbnailWithPlaceholder: React.FC<ThumbnailProps> = ({ src, alt }) => {
//     const [loaded, setLoaded] = useState(false);

//     const imageSrc = src || 'asset/images/product-placeholder.png';

//     return (
//         <div className="product-thumb-wrapper">
//             {/* Placeholder */}
//             {!loaded && (
//                 <img
//                     src="asset/images/product-placeholder.png"
//                     alt="placeholder"
//                     className="product-thumb-img"
//                 />
//             )}

//             {/* Actual Image */}
//             <img
//                 src={imageSrc}
//                 alt={alt}
//                 className="product-thumb-img"
//                 style={{ display: loaded ? 'block' : 'none' }}
//                 onLoad={() => setLoaded(true)}
//             />
//         </div>
//     );
// };

// /* ================= COMPONENT ================= */

// const FeaturedProducts: React.FC = () => {
//     const navigate = useNavigate();
//     const [products, setProducts] = useState<Product[]>([]);
//     const [loading, setLoading] = useState(true);
//     const { formatPrice, currencySymbol, loading: companyLoading } = useCompany();

//     useEffect(() => {
//         loadFeaturedProducts();
//     }, []);

//     const loadFeaturedProducts = async () => {
//         try {
//             setLoading(true);
//             const featuredProducts = await fetchFeaturedProducts();
//             setProducts(featuredProducts);
//         } catch (error) {
//             console.error('Error loading featured products:', error);
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleProductClick = (productId: string) => {
//         // Updated to match the route in App.tsx: /product/:id
//         navigate(`/product/${productId}`);
//     };

//     /* ================= LOADING ================= */

//     if (loading) {
//         return (
//             <div className="p-t-0">
//                 <h4 className="mtext-112 cl2 p-b-33">Featured Products</h4>
//                 <p className="stext-116 cl6">Loading...</p>
//             </div>
//         );
//     }

//     if (products.length === 0) {
//         return null;
//     }

//     /* ================= RENDER ================= */

//     return (
//         <div className="p-t-0">
//             <h4 className="mtext-112 cl2 p-b-33">Featured Products</h4>

//             <ul>
//                 {products.map(product => {
//                     const displayInfo = getProductDisplayInfo(product);
//                     const imageUrl = product.images?.[0];

//                     return (
//                         <li key={product.id} className="flex-w flex-t p-b-30">
//                             {/* IMAGE */}
//                             <a
//                                 onClick={() => handleProductClick(product.id)}
//                                 className="wrao-pic-w size-214 hov-ovelay1 m-r-20"
//                                 style={{ cursor: 'pointer' }}
//                             >
//                                 <ThumbnailWithPlaceholder
//                                     src={imageUrl}
//                                     alt={product.name}
//                                 />
//                             </a>

//                             {/* CONTENT */}
//                             <div className="size-215 flex-col-t p-t-8">
//                                 <a
//                                     onClick={() => handleProductClick(product.id)}
//                                     className="stext-116 cl8 hov-cl1 trans-04"
//                                     style={{ cursor: 'pointer' }}
//                                 >
//                                     {product.name}
//                                 </a>

//                                 <span className="stext-116 cl6 p-t-20">
//                                     {formatPrice(displayInfo.price)}
//                                 </span>
//                             </div>
//                         </li>
//                     );
//                 })}
//             </ul>
//         </div>
//     );
// };

// export default FeaturedProducts;